import { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation, Navigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import ProductListing from "@/components/ProductListing";
import Seo from "@/components/Seo";
import SchemaLd from "@/components/SchemaLd";
import NotFoundPage from "@/pages/NotFoundPage";
import { Skeleton } from "@/components/ui/skeleton";
import { ChevronLeft, ChevronRight, Truck } from "lucide-react";

export default function CategoryPage() {
  const { name } = useParams();
  const isAll = (name || "All") === "All";
  const location = useLocation();
  const [cat, setCat] = useState(isAll ? { name: "All", slug: "All" } : null);
  const [missing, setMissing] = useState(false);
  const [seo, setSeo] = useState(null);
  const navigate = useNavigate();
  const settings = useSettings();

  useEffect(() => {
    window.scrollTo(0, 0);
    setMissing(false);
    if (isAll) { setCat({ name: "All", slug: "All" }); return; }
    setCat(null);
    api.get(`/categories/resolve/${encodeURIComponent(name)}`).then((r) => setCat(r.data))
      .catch(() => setMissing(true));
  }, [name, isAll]);

  useEffect(() => {
    if (!cat || cat.redirect) return;
    if (!isAll) api.get("/seo/page", { params: { type: "category", key: cat.id } }).then((r) => setSeo(r.data.effective)).catch(() => setSeo(null));
  }, [cat, isAll]);

  if (missing) return <NotFoundPage />;
  if (cat?.redirect) return <Navigate to={`/category/${cat.slug}${location.search}`} replace />;
  const label = cat ? (isAll ? "All Products" : cat.name) : "";

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header />
      {cat && (
        <Seo title={seo?.seo_title || label}
          description={seo?.meta_description || `Shop ${isAll ? "all products" : cat.name} at ACP Online — New Zealand trade supplier. Trade pricing, fast NZ delivery and GST tax invoices.`}
          image={seo?.og_image || undefined} canonical={isAll ? undefined : `/category/${cat.slug}`}
          noindex={seo?.seo_index === "noindex"} />
      )}
      {cat && !isAll && <SchemaLd type="category" ekey={cat.id} />}
      <section className="bg-steel text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <button onClick={() => navigate("/")} data-testid="category-back" className="flex items-center gap-1 text-sm text-slate-300 hover:text-accent mb-3">
            <ChevronLeft size={16} /> All categories
          </button>
          {cat?.parents?.length > 0 && (
            <nav className="flex flex-wrap items-center gap-1 text-xs text-slate-300 mb-2" data-testid="category-breadcrumbs">
              {cat.parents.map((p) => (
                <span key={p.slug} className="flex items-center gap-1">
                  <Link to={`/category/${p.slug}`} className="hover:text-accent">{p.name}</Link><ChevronRight size={12} />
                </span>
              ))}
            </nav>
          )}
          <span className="text-xs font-mono tracking-[0.3em] text-accent">CATEGORY</span>
          <h1 className="mt-2 text-4xl sm:text-5xl font-extrabold" data-testid="category-heading">{cat ? (isAll ? "All" : cat.name) : <Skeleton className="h-12 w-64" />}</h1>
          {cat?.description && <p className="mt-3 text-slate-200 max-w-3xl text-sm" data-testid="category-description">{cat.description}</p>}
          <p className="mt-3 text-slate-300 flex items-center gap-2 text-sm">
            <Truck size={15} className="text-accent" /> {settings?.free_shipping_public_message || "FREE NZ shipping on orders $1,000+"}
          </p>
        </div>
      </section>
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 w-full flex-1">
        {cat && !cat.redirect && (
          <ProductListing params={isAll ? {} : { category_id: cat.id, category: cat.name }} testid="category-product"
            empty={<div className="text-center py-20">
              <p className="text-muted-foreground">No products in this category yet.</p>
              <button onClick={() => navigate("/")} className="mt-4 text-accent hover:underline">Browse all products →</button>
            </div>} />)}
      </main>
      <Footer />
    </div>
  );
}
